import { StatusBadge } from '@abb-procure/common';
import { BooleanInput } from '@angular/cdk/coercion';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { SearchInsightsService } from '../../services/search-insights.service';
import { SearchState } from '../../store/search.state';
import { SearchBaseDto } from '../../types/search-base-dto.type';
import { SearchResultItemDto } from '../../types/search-result-item-dto.type';
import { SearchEntityBadge } from '../search-entity-badge/search-entity-badge.component';
import { SearchResultDetail } from '../search-result-detail/search-result-detail.component';
import { SearchResultHighlights } from '../search-result-highlights/search-result-highlights.component';

@Component({
  selector: 'procure-search-result-item',
  templateUrl: './search-result-item.component.html',
  styleUrl: './search-result-item.component.scss',
  imports: [
    RouterLink,
    StatusBadge,
    SearchEntityBadge,
    SearchResultHighlights,
    SearchResultDetail,
    MatIcon,
    OverlayModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultItem {
  readonly itemToggle = viewChild.required('item', { read: ElementRef });

  readonly index = input.required<number>();
  readonly entityType = input.required<string | null | undefined>();
  readonly item = input.required<SearchBaseDto | SearchResultItemDto>();

  readonly showDetail = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  protected readonly isDetailVisible = signal(false);

  protected readonly link = computed(() => {
    const item = this.item();

    switch (item.entityType) {
      case 'SUP':
        return `/supplier/${item.id}`;
      case 'LDI':
        return `/organisation/local-divisions/${item.id}`;
      case 'HUB':
        return `/organisation/hubs/${item.id}`;
      case 'DIV':
        return `/organisation/divisions/${item.id}`;
      default:
        return item.entityNumber;
    }
  });

  protected readonly highlights = computed(
    () => (this.item() as SearchResultItemDto).highlights ?? {},
  );

  protected readonly state = inject(SearchState);

  private readonly insightsService = inject(SearchInsightsService);

  protected toggleDetail(): void {
    this.isDetailVisible.update((value) => !value);

    if (!this.isDetailVisible()) {
      return;
    }
    const index = this.index();

    if (index === null) {
      return;
    }

    this.insightsService.logEvent({
      name: 'Click Search Info',
      rank: index,
      item: this.item(),
    });

    this.isDetailVisible.set(true);
  }

  protected clickLink(): void {
    this.state.hideSearch();

    this.insightsService.logEvent({
      name: 'Click Search Routing',
      rank: this.index(),
      item: this.item(),
    });
  }
}
